import { useEffect, useRef, type Ref } from 'react';
import { getScrollTag } from '../../features/uiState/uiState.selectors';
import { setScrollTag } from '../../features/uiState/uiState.slice';
import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { type ScrollTag } from '.';

/**
 * Scrolls element with specified scrollTag into view when target value changes in redux state.
 * @param scrollTag - The scroll tag to match against the redux state value.
 * @param scrollIntoViewOptions - Options to pass to the scrollIntoView method.
 * @returns A ref to the element that should be scrolled into view.
 */
export const useScrollToTag = (
  scrollTag?: ScrollTag,
  scrollIntoViewOptions?: ScrollIntoViewOptions,
): Ref<HTMLElement> => {
  const targetRef = useRef<HTMLElement | null>(null);

  const target = useAppSelector(getScrollTag);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const isInvalidScrollTag =
      scrollTag === undefined ||
      !targetRef.current ||
      !Boolean(target) ||
      scrollTag !== target;

    if (isInvalidScrollTag) {
      return;
    }

    targetRef.current?.scrollIntoView({
      behavior: 'smooth',
      ...scrollIntoViewOptions,
    });
    dispatch(setScrollTag(null));
  }, [target]);

  return targetRef;
};
